import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Box, Typography, InputAdornment,
    Checkbox, FormControlLabel, useMediaQuery,
} from '@mui/material';
import Tooltip from '../ui/tooltip';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import theme from '../../util/theme/theme';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useNavigate } from 'react-router-dom';

interface AddCategoryPopupProps {
    open: boolean;
    onClose: () => void;
    onCreate: (data: { code: string, name: string, searchStrings: string, parentId?: number, defaultTaxIds: number[] }) => void;
    isChild?: boolean;
    parent?: { code: string, id: number } | null;
    isEdit?: { id: number | null, state: boolean };
    takenData?: { code: string, name: string, searchStrings: string }[] | [];
    entityId?: number;
}

const AddCategoryPopup: React.FC<AddCategoryPopupProps> = ({ open, onClose, onCreate, isChild, parent, isEdit, takenData, entityId }) => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [categoryCode, setCategoryCode] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [searchStrings, setSearchStrings] = useState('');
    const [selectedTaxIds, setSelectedTaxIds] = useState<number[]>([]);
    const [errors, setErrors] = useState<{ code?: string; name?: string; searchStrings?: string }>({});

    const [fetchCategory, setFetchCategory] = useApi<any>();
    const [fetchTaxes, setFetchTaxes] = useApi<any>();

    const taxes: { id: number; name: string }[] = React.useMemo(() => {
        const data = fetchTaxes.data;
        if (!data) return [];
        const list = Array.isArray(data) ? data : data.taxDefinitions ?? [];
        return list.filter((t: any) => t.isActive && t.isEnabled);
    }, [fetchTaxes.data]);

    React.useEffect(() => {
        setCategoryCode('');
        setCategoryName('');
        setSearchStrings('');
        setErrors({});

        if (isEdit?.state && isEdit.id) {
            setFetchCategory(buildApiRequestConfig(routes.categories.fetchById, [{ param: 'id', value: isEdit.id }]));
        }
    }, [isEdit, open]);

    React.useEffect(() => {
        if (entityId) {
            setFetchTaxes(buildApiRequestConfig(routes.taxDefinitions.fetchByEntity, [{ param: 'entityId', value: entityId }]));
        }
    }, [entityId]);

    React.useEffect(() => {
        const { data, isLoading } = fetchCategory;
        if (data && !isLoading) {
            setCategoryCode(data.code);
            setCategoryName(data.name);
            setSearchStrings(data.searchString);
            setSelectedTaxIds(data.defaultTaxIds ?? []);
        }
    }, [fetchCategory]);

    const toggleTax = (id: number) => {
        setSelectedTaxIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const validate = () => {
        const newErrors: { code?: string; name?: string; searchStrings?: string } = {};
        if (!categoryCode) {
            newErrors.code = 'Category code is required';
        } else {
            let isDuplicateCode = false;
            if (!isChild || !parent) {
                const isTaken = takenData?.find(cat => cat.code === categoryCode);
                if (isTaken && (!isEdit?.state || (isEdit?.state && isEdit.id && isTaken.code !== categoryCode))) {
                    isDuplicateCode = true;
                }
            }
            if (isDuplicateCode) newErrors.code = 'Category code is already taken';
        }
        if (!categoryName) {
            newErrors.name = 'Category name is required';
        } else {
            const isTaken = takenData?.find(cat => cat.name.toLowerCase() === categoryName.toLowerCase());
            if (isTaken && (!isEdit?.state || (isEdit?.state && isEdit.id && isTaken.name !== categoryName))) {
                newErrors.name = 'Category name is already taken';
            }
        }
        if (!searchStrings) {
            newErrors.searchStrings = 'Search strings are required';
        } else {
            const inputStrings = searchStrings?.split(',')?.map(s => s.trim().toLowerCase()).filter(Boolean);
            const isTaken = takenData?.find(cat => {
                const existingStrings = cat.searchStrings?.split(',')?.map(s => s.trim().toLowerCase()).filter(Boolean);
                return inputStrings.some(str => existingStrings?.includes(str));
            });
            if (isTaken && (!isEdit?.state || (isEdit?.state && isEdit.id && isTaken.searchStrings !== searchStrings))) {
                newErrors.searchStrings = 'One or more search strings are already taken';
            }
        }
        setErrors(newErrors);
        return !!(newErrors.code || newErrors.name || newErrors.searchStrings);
    };

    const handleCreate = () => {
        const hasErrors = validate();
        if (!hasErrors) {
            onCreate({ code: categoryCode, name: categoryName, searchStrings, parentId: parent?.id, defaultTaxIds: selectedTaxIds });
            setErrors({});
        }
    };

    const handleClose = () => {
        setCategoryCode('');
        setCategoryName('');
        setSearchStrings('');
        setSelectedTaxIds([]);
        setErrors({});
        onClose();
    };

    const content = (
        <>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>
            {isEdit?.state
            ? `Edit ${isChild ? 'Subcategory' : 'Category'}`
            : `Add New ${isChild ? 'Subcategory' : 'Category'}`}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
            <Box mb={2}>
                <Typography fontSize={14} color='var(--color-text-muted)' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Category Code
                    <span style={{ color: '#F44336' }}>*</span>
                    <Tooltip message="Enter any code format you prefer. Hyphens are allowed anywhere.Example: 1111-1000" />
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter category code"
                    value={categoryCode}
                    onChange={e => setCategoryCode(e.target.value)}
                    error={!!errors.code}
                    helperText={errors.code}
                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    InputProps={{
                        style: { fontSize: 16 },
                        startAdornment: isChild &&
                            <InputAdornment position="start" sx={{ background: '#45454532', borderRadius: 1, fontSize: 10, px: 0.5, color: 'var(--color-default-text)' }}>
                                {parent?.code?.replaceAll('-', ' − ')} −
                            </InputAdornment>,
                    }}
                />
            </Box>
            <Box mb={2}>
                <Typography fontSize={14} color='var(--color-text-muted)'>
                    Category Name <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter category name"
                    value={categoryName}
                    onChange={e => setCategoryName(e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                />
            </Box>
            <Box mb={2}>
                <Typography fontSize={14} color='var(--color-text-muted)'>
                    Search Strings <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={4}
                    placeholder="Enter search strings"
                    value={searchStrings}
                    size='small'
                    onChange={e => setSearchStrings(e.target.value)}
                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    error={!!errors.searchStrings}
                    helperText={errors.searchStrings}
                    InputProps={{ style: { overflowY: 'auto' } }}
                />
            </Box>
            <Box>
                <Typography fontSize={14} color='var(--color-text-muted)' mb={0.5}>
                    Tax Applied by default <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                {taxes.length > 0 ? (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {taxes.map(tax => (
                            <FormControlLabel
                                key={tax.id}
                                control={
                                    <Checkbox
                                        checked={selectedTaxIds.includes(tax.id)}
                                        onChange={() => toggleTax(tax.id)}
                                        size="small"
                                        sx={{ color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                    />
                                }
                                label={<Typography fontSize={14}>{tax.name}</Typography>}
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography sx={{
                        fontSize: 12,
                        fontStyle: 'italic',
                        textAlign: 'center',
                        border: '1px solid #514e0030',
                        borderRadius: 1,
                        py: 0.75,
                        background: '#fff70010'
                    }}>No tax definitions yet. <span style={{ textDecoration: 'underline', color: 'var(--color-primary)', cursor: 'pointer' }} onClick={() => navigate('/tax-definitions')}>Add here</span></Typography>
                )
                }
            </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                Cancel
            </Button>
            <Button 
                onClick={handleCreate} 
                variant="contained" 
                sx={{ 
                    background: 'var(--color-primary)', 
                    color: '#fff', 
                    boxShadow: 'none', 
                    borderRadius: 2, 
                    fontWeight: 600, 
                    '&:hover': { background: 'var(--color-primary)' } 
                }}>
                {isEdit?.state ? 'Save Changes' : parent ? 'Add Subcategory' : 'Add Category'}
            </Button>
        </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default AddCategoryPopup;
